import { useState, useEffect } from 'react';
import { getLists, addListEntry, removeListEntry, publishLists, type ListEntry } from '../api/client';
import { format } from 'date-fns';

const ENTRY_TYPES = ['IP', 'IP_RANGE', 'ASN', 'COUNTRY', 'DEVICE_HASH', 'EMAIL_HASH', 'PHONE_HASH'];

export default function ListsPage() {
  const [entries, setEntries] = useState<ListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ALLOW' | 'DENY'>('DENY');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({
    list_type: 'DENY' as 'ALLOW' | 'DENY',
    entry_type: 'IP',
    value: '',
    notes: '',
    expires_at: '',
  });

  useEffect(() => {
    loadLists();
  }, [activeTab]);

  async function loadLists() {
    setLoading(true);
    setError(null);

    const result = await getLists(activeTab);

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setEntries(result.data.entries);
    }

    setLoading(false);
  }

  async function handleAddEntry() {
    if (!newEntry.value) {
      setError('Value is required');
      return;
    }

    const result = await addListEntry({
      list_type: newEntry.list_type,
      entry_type: newEntry.entry_type,
      value: newEntry.value,
      notes: newEntry.notes || undefined,
      expires_at: newEntry.expires_at || undefined,
    });

    if (result.error) {
      setError(result.error);
    } else {
      setShowAddForm(false);
      setNewEntry({
        list_type: activeTab,
        entry_type: 'IP',
        value: '',
        notes: '',
        expires_at: '',
      });
      loadLists();
      setSuccess('Entry added successfully');
      setTimeout(() => setSuccess(null), 3000);
    }
  }

  async function handleRemoveEntry(id: number) {
    if (!confirm('Are you sure you want to remove this entry?')) return;

    const result = await removeListEntry(id);

    if (result.error) {
      setError(result.error);
    } else {
      loadLists();
      setSuccess('Entry removed');
      setTimeout(() => setSuccess(null), 3000);
    }
  }

  async function handlePublish() {
    const result = await publishLists();

    if (result.error) {
      setError(result.error);
    } else {
      setSuccess(`Lists published: ${result.data?.allow_count} allow, ${result.data?.deny_count} deny`);
      setTimeout(() => setSuccess(null), 5000);
    }
  }

  const allowCount = entries.filter((e) => e.list_type === 'ALLOW').length;
  const denyCount = entries.filter((e) => e.list_type === 'DENY').length;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Allow/Deny Lists</h1>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={handlePublish}
          >
            Publish to KV
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setNewEntry({ ...newEntry, list_type: activeTab });
              setShowAddForm(true);
            }}
          >
            Add Entry
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-4">
          {error}
          <button onClick={() => setError(null)} className="ml-4 underline">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-500/20 text-green-400 p-4 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          className={`px-4 py-2 rounded-lg ${
            activeTab === 'DENY'
              ? 'bg-red-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => setActiveTab('DENY')}
        >
          Deny List ({denyCount})
        </button>
        <button
          className={`px-4 py-2 rounded-lg ${
            activeTab === 'ALLOW'
              ? 'bg-green-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => setActiveTab('ALLOW')}
        >
          Allow List ({allowCount})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Value</th>
                <th>Notes</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-gray-400 py-8">
                    No entries in this list
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <span className="bg-gray-700 px-2 py-1 rounded text-sm">
                        {entry.entry_type}
                      </span>
                    </td>
                    <td className="font-mono">{entry.value}</td>
                    <td className="text-gray-400">{entry.notes || '-'}</td>
                    <td>
                      {entry.expires_at
                        ? format(new Date(entry.expires_at), 'MMM d, yyyy HH:mm')
                        : 'Never'}
                    </td>
                    <td>
                      <button
                        onClick={() => handleRemoveEntry(entry.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add entry modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold">Add List Entry</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">List Type</label>
                <select
                  className="select"
                  value={newEntry.list_type}
                  onChange={(e) =>
                    setNewEntry({ ...newEntry, list_type: e.target.value as 'ALLOW' | 'DENY' })
                  }
                >
                  <option value="DENY">Deny</option>
                  <option value="ALLOW">Allow</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Entry Type</label>
                <select
                  className="select"
                  value={newEntry.entry_type}
                  onChange={(e) => setNewEntry({ ...newEntry, entry_type: e.target.value })}
                >
                  {ENTRY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Value *</label>
                <input
                  type="text"
                  className="input"
                  placeholder={
                    newEntry.entry_type === 'IP'
                      ? '192.168.1.1'
                      : newEntry.entry_type === 'IP_RANGE'
                      ? '192.168.0.0/16'
                      : newEntry.entry_type === 'ASN'
                      ? 'AS12345'
                      : newEntry.entry_type === 'COUNTRY'
                      ? 'US'
                      : 'hash value'
                  }
                  value={newEntry.value}
                  onChange={(e) => setNewEntry({ ...newEntry, value: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Reason for adding this entry"
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Expires At (optional)</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={newEntry.expires_at}
                  onChange={(e) => setNewEntry({ ...newEntry, expires_at: e.target.value })}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddEntry}>
                Add Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
